import React from "react";

interface SignatureSuccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  signedPdfUrl: string;
  quoteName: string;
}

export function SignatureSuccessModal({
  isOpen,
  onClose,
  signedPdfUrl,
  quoteName,
}: SignatureSuccessModalProps) {
  if (!isOpen) return null;

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = signedPdfUrl;
    link.download = `${quoteName}_signe.pdf`;
    link.target = "_blank";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      {/* Overlay avec confettis en arrière-plan */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] animate-fade-in"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 pointer-events-none">
        <div
          className="bg-white rounded-2xl shadow-2xl w-full max-w-lg flex flex-col pointer-events-auto animate-scale-in"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header avec animation */}
          <div className="relative bg-gradient-to-r from-green-500 to-emerald-600 text-white p-6 rounded-t-2xl overflow-hidden">
            {/* Confettis décoratifs en CSS */}
            <div className="absolute inset-0 opacity-20">
              {[...Array(20)].map((_, i) => (
                <div
                  key={i}
                  className="absolute w-2 h-2 bg-white rounded-full animate-float"
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                    animationDelay: `${Math.random() * 2}s`,
                    animationDuration: `${2 + Math.random() * 2}s`,
                  }}
                />
              ))}
            </div>

            <div className="relative z-10">
              <div className="flex items-center justify-center mb-3">
                <div className="text-6xl animate-bounce-slow">🎉</div>
              </div>
              <h2 className="text-2xl font-bold text-center mb-2">
                Félicitations !
              </h2>
              <p className="text-center text-green-100 text-sm">
                Votre devis a été signé avec succès
              </p>
            </div>

            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-white/80 hover:text-white text-3xl font-light leading-none transition-colors z-20"
            >
              ×
            </button>
          </div>

          {/* Content */}
          <div className="p-8">
            {/* Icône de succès géante */}
            <div className="text-center mb-6">
              <div className="text-8xl mb-4 animate-bounce-slow">📄</div>
              <p className="text-lg text-gray-700 mb-2">
                Votre devis est maintenant <strong className="text-green-600">signé électroniquement</strong>
              </p>
            </div>

            {/* Info box */}
            <div className="bg-blue-50 border-l-4 border-blue-500 p-5 rounded-r-lg mb-4">
              <div className="flex items-start">
                <div className="flex-shrink-0 text-3xl mr-4">📧</div>
                <div>
                  <p className="text-base font-semibold text-blue-900 mb-1">
                    Confirmation envoyée par email
                  </p>
                  <p className="text-sm text-blue-700">
                    Vous recevrez votre <strong>facture d'acompte</strong> dans les plus brefs délais.
                  </p>
                </div>
              </div>
            </div>

            {/* Avertissement important */}
            <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-lg mb-6">
              <div className="flex items-start">
                <div className="flex-shrink-0 text-2xl mr-3">⚠️</div>
                <div>
                  <p className="text-sm text-amber-800 leading-relaxed">
                    <strong>Important :</strong> La signature de ce devis ne confirme pas définitivement votre événement. 
                    Nous vous enverrons une confirmation une fois la disponibilité de la date et du créneau horaire vérifiée.
                  </p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-col gap-3">
              <button
                onClick={handleDownload}
                className="w-full px-8 py-4 text-base font-bold text-white bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl hover:from-indigo-700 hover:to-purple-700 transition-all shadow-lg hover:shadow-2xl transform hover:scale-105 flex items-center justify-center gap-3"
              >
                <svg
                  className="w-6 h-6"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                Télécharger mon devis signé
              </button>
              <button
                onClick={onClose}
                className="w-full px-6 py-3 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes scale-in {
          from {
            transform: scale(0.9);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }

        @keyframes bounce-slow {
          0%,
          100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }

        @keyframes float {
          0%,
          100% {
            transform: translateY(0) rotate(0deg);
          }
          50% {
            transform: translateY(-20px) rotate(180deg);
          }
        }

        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }

        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }

        .animate-bounce-slow {
          animation: bounce-slow 2s ease-in-out infinite;
        }

        .animate-float {
          animation: float linear infinite;
        }
      `}</style>
    </>
  );
}

